import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "../lib/api";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Heart, Users } from "lucide-react";

export default function Auth() {
  const { t } = useLang();
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const invite = params.get("invite");
  const [mode, setMode] = useState(invite ? "register" : "login");
  const [form, setForm] = useState({ email: "", password: "", name: "", org_name: "" });
  const [busy, setBusy] = useState(false);
  const [inviteOrg, setInviteOrg] = useState(null);
  useDocumentTitle(mode === "login" ? t("login") : t("free_register"));

  useEffect(() => {
    if (!invite) return;
    setMode("register");
    api.get(`/invite/${invite}`).then(({ data }) => {
      if (data.valid) setInviteOrg(data.org_name);
    }).catch(() => {});
  }, [invite]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = mode === "login"
      ? await login(form.email, form.password)
      : await register({ email: form.email, password: form.password, name: form.name, org_name: form.org_name || undefined, invite: invite || undefined });
    setBusy(false);
    if (res.ok) { toast.success(t("app_name")); nav("/journey"); }
    else toast.error(res.error);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto flex max-w-md flex-col px-5 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl border border-border bg-card p-8 shadow-xl shadow-primary/5">
          <div className="mb-6 flex flex-col items-center text-center">
            <span className="mb-3 grid h-14 w-14 place-items-center rounded-2xl bg-primary text-primary-foreground">
              <Heart className="h-7 w-7" fill="currentColor" />
            </span>
            <h1 className="font-display text-2xl font-bold text-primary">
              {mode === "login" ? t("login") : t("free_register")}
            </h1>
          </div>

          {inviteOrg && (
            <div data-testid="invite-banner" className="mb-4 flex items-center gap-2 rounded-xl border border-primary/20 bg-secondary px-4 py-3 text-sm font-semibold text-primary">
              <Users className="h-4 w-4 shrink-0" /> {t("joining_org")}: {inviteOrg}
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label>{t("name")}</Label>
                <Input data-testid="auth-name" required value={form.name} onChange={set("name")} />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>{t("email")}</Label>
              <Input data-testid="auth-email" type="email" required value={form.email} onChange={set("email")} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("password")}</Label>
              <Input data-testid="auth-password" type="password" required value={form.password} onChange={set("password")} />
            </div>
            {mode === "register" && !invite && (
              <div className="space-y-1.5">
                <Label>{t("org_name")}</Label>
                <Input data-testid="auth-orgname" value={form.org_name} onChange={set("org_name")} />
              </div>
            )}
            <Button data-testid="auth-submit" type="submit" disabled={busy} className="h-11 w-full rounded-full text-base">
              {busy ? "…" : mode === "login" ? t("login") : t("free_register")}
            </Button>
          </form>

          <button
            data-testid="auth-toggle"
            onClick={() => setMode((m) => (m === "login" ? "register" : "login"))}
            className="mt-5 w-full text-center text-sm font-semibold text-primary hover:underline">
            {mode === "login" ? `${t("no_account")} ${t("register")}` : `${t("has_account")} ${t("login")}`}
          </button>
        </motion.div>
      </div>
    </div>
  );
}
